export interface Call {
  id: string;
  address: string;
  dispatchedTime: string;
  status:
    | 'ready'
    | 'waiting'
    | 'en_route'
    | 'on_scene'
    | 'towing'
    | 'complete'
    | 'canceled';
  accountInfo: string;
  customerId?: string;
  driverId?: string;
  priority?: 'low' | 'normal' | 'high' | 'emergency';
  pickupLocation?: Location;
  dropoffLocation?: Location;
  vehicleInfo?: VehicleInfo;
  vehicleId?: string;
  description?: string;
  estimatedArrival?: string;
  actualArrival?: string;
  completedAt?: string;
  totalCost?: number;
  createdAt?: string;
  updatedAt?: string;
  dispatchNumber: string;
  type: string;
  contactName: string;
  contactPhone: string;
  pickupLocationLat: number;
  pickupLocationLong: number;
  destinationLat: number;
  destinationLong: number;
  scheduledAt: string | null;
  completionTime: string | null;
  mileage: number | null;
  totalCharges: number | null;
  startedAt: string | null;
  arrivedAt: string | null;
  notes: string | null;
  billingNotes: string | null;
  includeBillingNotes: boolean;
  account: string;
  vehicle: Vehicle;
  version?: number; // Version field for concurrency control
  source?: 'MANUAL' | 'CHAT' | 'SCHEDULED' | 'URGENTLY' | 'EXTERNAL_API';
  sourceMetadata?: Record<string, any> | null;
}

export interface Task {
  id: string;
  taskTitle: string;
  description?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: number;
  dueDate?: Date;
  assignedToId: string;
  notes?: any;
  completedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  isRepeat: boolean;
  repeatInterval?: 'DAYS' | 'WEEKS' | 'MONTHS';
  repeatDuration?: number;
}

export interface Driver {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  licenseNumber: string;
  vehicleInfo: VehicleInfo;
  status: DriverStatus;
  location?: Location;
  createdAt: string;
  updatedAt: string;
  companyId?: string;
}

export interface VehicleInfo {
  make: string;
  model: string;
  year: number;
  licensePlate: string;
  color: string;
  vin?: string;
  towingCapacity?: number;
}

export type DriverStatus =
  | 'ACTIVE'
  | 'CLEAR'
  | 'DISPATCHED'
  | 'ON_LEAVE'
  | 'WAITING'
  | 'EN_ROUTE'
  | 'ON_SCENE'
  | 'TOWING'
  | 'RETURNING'
  | 'OFFLINE'
  | 'unknown';

export interface Location {
  latitude: number;
  longitude: number;
  heading?: number;
  timestamp?: string;
  address?: string;
  notes?: string;
}

// Navigation types for React Router
export interface RouteParams {
  '/': undefined;
  '/calls': undefined;
  '/calls/:id': { id: string };
  '/profile': undefined;
  '/settings': undefined;
  '/impounds': undefined;
}

// API Response types
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
  error?: string;
}

// Filter and search types
export interface CallFilters {
  status?: Call['status'][];
  priority?: Call['priority'][];
  dateRange?: {
    start: string;
    end: string;
  };
  searchQuery?: string;
}

// Enhanced Impound interface matching backend structure
export interface Impound {
  id: string;
  impoundNumber: string;
  dispatchId: string;
  invoiceId?: string | null;
  driverId?: string | null;
  companyId: string;
  locationId?: string | null;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: number;
  vehicleColor: string;
  vehicleOdometer: number | null;
  licensePlate: string;
  vin: string;
  impoundDate: string;
  /** Release timestamp when status is RELEASED (from API releasedDate) */
  releaseAt: string | null;
  daysHeld: number;
  total: number;
  balanceDue: number;
  lotLocation: string; // This maps to storageLot from backend
  status: 'ACTIVE' | 'RELEASED';
  completedByCurrentDriver?: boolean;
  createdAt: string;
  updatedAt: string;

  // Associated data
  dispatch?: {
    id: string;
    dispatchNumber: string;
    type: string;
    contactName: string;
    contactPhone: string;
    pickupLocation: string;
    destination: string;
    priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
    account: string;
    notes?: string | null;
  };

  driver?: {
    id: string;
    name: string;
    email: string;
    phone: string;
    truckNumber: string;
  };

  invoice?: {
    id: string;
    invoiceNumber: string;
    total: number;
    balanceDue: number;
  };
}

export type SortOption = 'Latest' | 'Oldest' | 'Completed by you';

// Import the backend types
import { DriverImpound, DriverTask } from '../services/driverApi';

// Mapping function to convert DriverImpound to Impound
export const mapDriverImpoundToImpound = (
  driverImpound: DriverImpound
): Impound => {
  return {
    id: driverImpound.id,
    impoundNumber: driverImpound.impoundNumber,
    dispatchId: driverImpound.dispatchId,
    invoiceId: driverImpound.invoiceId,
    driverId: driverImpound.driverId,
    companyId: driverImpound.companyId,
    locationId: driverImpound.locationId,
    vehicleMake: driverImpound.dispatch?.vehicle?.vehicleName || 'Unknown',
    vehicleModel: driverImpound.dispatch?.vehicle?.vehicleModel || 'Unknown',
    vehicleYear: driverImpound.dispatch?.vehicle?.vehicleYear || 0,
    vehicleColor:
      (driverImpound.dispatch?.vehicle?.vehicleAttributes as any)?.color ||
      'Unknown',
    vehicleOdometer: driverImpound.dispatch?.vehicle?.odometer ?? null,
    licensePlate: driverImpound.dispatch?.vehicle?.licensePlate || '',
    vin: driverImpound.dispatch?.vehicle?.vin || '',
    impoundDate: driverImpound.impoundDate,
    releaseAt: driverImpound.releasedDate ?? null,
    daysHeld: driverImpound.daysHeld,
    total: driverImpound.total,
    balanceDue: driverImpound.balanceDue,
    lotLocation: driverImpound.storageLot,
    status: driverImpound.status,
    completedByCurrentDriver: driverImpound.driverId !== null, // If driverId matches current driver
    createdAt: driverImpound.createdAt,
    updatedAt: driverImpound.updatedAt,
    dispatch: driverImpound.dispatch
      ? {
          id: driverImpound.dispatch.id,
          dispatchNumber: driverImpound.dispatch.dispatchNumber,
          type: driverImpound.dispatch.type,
          contactName: driverImpound.dispatch.contactName,
          contactPhone: driverImpound.dispatch.contactPhone,
          pickupLocation: driverImpound.dispatch.pickupLocation,
          destination: driverImpound.dispatch.destination,
          priority: driverImpound.dispatch.priority,
          account: driverImpound.dispatch.account,
          notes: driverImpound.dispatch.notes,
        }
      : undefined,
    driver: driverImpound.driver || undefined,
    invoice: driverImpound.invoice || undefined,
  };
};

// Mapping function to convert DriverTask to Task
export const mapDriverTaskToTask = (driverTask: DriverTask): Task => {
  // Map backend status to frontend status
  let status: Task['status'] = 'pending';
  if (driverTask.status === 'PENDING') status = 'pending';
  else if (driverTask.status === 'IN_PROGRESS') status = 'in_progress';
  else if (driverTask.status === 'COMPLETED') status = 'completed';
  else if (driverTask.status === 'CANCELLED') status = 'cancelled';

  return {
    id: driverTask.id,
    taskTitle: driverTask.taskTitle,
    description: driverTask.description,
    status,
    priority: driverTask.priority,
    dueDate: driverTask.dueDate,
    assignedToId: driverTask.assignedToId,
    notes: driverTask.notes,
    completedAt: driverTask.completedAt,
    createdAt: driverTask.createdAt,
    updatedAt: driverTask.updatedAt,
    isRepeat: driverTask.isRepeat,
    repeatInterval: driverTask.repeatInterval,
    repeatDuration: driverTask.repeatDuration,
  };
};

export interface Vehicle {
  id: string;
  licensePlate: string;
  vin: string;
  vehicleType: string;
  vehicleName: string | null;
  vehicleModel: string | null;
  vehicleYear: number | null;
  vehicleAttributes: {
    color?: string;
    [key: string]: any;
  } | null;
  odometer: number | null;
  vehicleImages: string[];
}

// VehicleNotes types
export interface UserResponseDTO {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  auth0Id: string;
  driver?: {
    id: string;
    name: string;
  };
}

export interface VehicleNote {
  id: string;
  noteText: string;
  userId: string;
  vehicleId: string;
  createTime: string;
  updatedAt: string;
  user: UserResponseDTO;
}

export interface AddVehicleNoteDTO {
  noteText: string;
}

export interface GetVehicleNotesResponseDTO {
  notes: VehicleNote[];
  total: number;
}

// Performance Metrics types
export interface DriverDailyMetricDto {
  /**
   * Date in YYYY-MM-DD format
   * @example "2025-12-09"
   */
  date: string;
  /**
   * Day of the week abbreviation
   * @example "Mon"
   */
  day: string;
  /**
   * Number of completed calls on this day
   * @example 5
   */
  completedCalls: number;
}

/** Matches backend DriverLeaderboardEntryDto; JSON may carry string numerics. */
export interface DriverLeaderboardEntryDto {
  driverId: string;
  driverName: string;
  rank: number;
  totalCompletedCalls: number;
}

export interface DriverLeaderboardResponseDto {
  leaderboard: DriverLeaderboardEntryDto[];
}

export interface DriverPerformanceMetricsDto {
  /**
   * Total number of completed calls
   * @example 25
   */
  totalCompletedCalls: number;
  /**
   * Average response time in minutes
   * @example 15
   */
  averageResponseTime: number;
  /**
   * Daily breakdown of completed calls
   */
  dailyMetrics?: DriverDailyMetricDto[];
}

// Export payment types
export * from './payments';

/**
 * Billing Limits and Plan Information DTO
 * Returned by the GET /stripe/limits endpoint
 */
export interface BillingLimitsDto {
  /**
   * The account type (NONE, STARTER, PRO, ENTERPRISE, DRIVER)
   * @example "PRO"
   */
  accountType: string | null;

  /**
   * Whether the account has an active subscription
   * @example true
   */
  isSubscribed: boolean;

  /**
   * Whether the account is currently in trial period
   * @example false
   */
  trialActive: boolean;

  /**
   * Trial end date in ISO format (null if not applicable)
   * @example "2025-05-01T00:00:00.000Z"
   */
  trialEndsAt: string | null;

  /**
   * The effective plan type based on subscription
   * @example "PRO"
   */
  planType: string | null;

  /**
   * Maximum number of drivers allowed (null for unlimited)
   * @example 10
   */
  driverLimit: number | null;

  /**
   * Maximum number of calls per month (null for unlimited)
   * @example 500
   */
  callLimit: number | null;

  /**
   * Current number of active drivers
   * @example 5
   */
  driversUsed: number;

  /**
   * Current number of calls this month
   * @example 127
   */
  callsUsed: number;

  /**
   * Whether Twilio SMS can be sent (feature available for non-ESSENTIAL plans)
   * @example true
   */
  canSendTwilio: boolean;

  /**
   * Whether multi-company feature is enabled for this account
   * @example false
   */
  multiCompany: boolean;
}
